import React, { useMemo, useEffect } from 'react';
import { CompRef } from '@wix/document-services-types';
import { create, FedopsLogger } from '@wix/fedops-logger';
import { EditorIntegrationAPI, WidgetSlot } from '../../types/widgetPlugins';

function sortWidgetSlots(widgetSlots: WidgetSlot[]): WidgetSlot[] {
  const getSlotRank = (slot: WidgetSlot): number =>
    slot.pluginInfo?.widgetId ? 0 : 1;

  // empty slots should be first
  return [...widgetSlots].sort(
    (slot1, slot2) => getSlotRank(slot2) - getSlotRank(slot1),
  );
}

interface IBaseWidgetDataContext {
  editorIntegrationAPI: EditorIntegrationAPI;
  widgetName: string;
  widgetRef?: CompRef;
}

export interface IWidgetDataContextInput extends IBaseWidgetDataContext {
  children?: React.ReactNode;
}

export interface IWidgetDataContextValue extends IBaseWidgetDataContext {
  widgetSlots: WidgetSlot[];
  fedopsLogger: FedopsLogger;
}

export function calcWidgetDataContextValue({
  editorIntegrationAPI,
  fedopsLogger,
  widgetRef,
  widgetName,
}: Omit<IWidgetDataContextValue, 'widgetSlots'>): IWidgetDataContextValue {
  const widgetSlots = widgetRef
    ? sortWidgetSlots(editorIntegrationAPI.getWidgetSlots(widgetRef))
    : [];

  return {
    editorIntegrationAPI,
    widgetRef,
    widgetName,
    widgetSlots,
    fedopsLogger,
  };
}

export const WidgetDataContext = React.createContext<IWidgetDataContextValue>(
  {} as IWidgetDataContextValue,
);

export const WidgetDataProvider: React.FC<IWidgetDataContextInput> = ({
  editorIntegrationAPI,
  widgetRef,
  widgetName,
  children,
}) => {
  const fedopsLogger = useMemo(
    () => create('editor-platform-host-integration'),
    [],
  );
  useEffect(() => {
    fedopsLogger.appLoadStarted();
  }, [fedopsLogger]);

  const widgetData = useMemo(
    () =>
      calcWidgetDataContextValue({
        editorIntegrationAPI,
        fedopsLogger,
        widgetRef,
        widgetName,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorIntegrationAPI, widgetRef?.id, fedopsLogger],
  );

  return (
    <WidgetDataContext.Provider value={widgetData}>
      {children}
    </WidgetDataContext.Provider>
  );
};
